/**
 * MySummaryMetrics — top-row KPI tiles for the member (self-scoped) Projects
 * dashboard:
 *   1. My Active Projects   (projects I'm a member of, not archived)
 *   2. My Open Tasks        (tasks assigned to / created by me, not done/closed)
 *   3. Upcoming Milestones  (milestones on my projects due in the next 30 days)
 *   4. At Risk              (my projects whose health is At Risk or Critical)
 *
 * Every query is keyed under ['projects', 'member', 'dashboard', ...] so the
 * single header refresh button can invalidate the whole dashboard in one shot.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { SimpleGrid, Skeleton } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import KpiCard from '@/components/advanced/Widgets/kpi/KpiCard';
import {
  IconFolders,
  IconChecklist,
  IconFlag,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  useProjectService,
  useProjectMilestoneService,
  useTaskPortalTaskService,
  type ProjectModelView,
  type ProjectMilestone,
  type TaskModelView,
} from '@actschurch/ccms-sdk';
import { PROJECTS_ROUTES, TASKS_ROUTES } from '@/config/routes';
import { computeHealthStatus, parseBackendDate } from '../../utils/projectHelpers';
import { MEMBER_DASHBOARD_KEY, OPEN_TASK_STATUS_IDS, MY_TASKS_FILTER } from './dashboardConstants';

export function MySummaryMetrics() {
  const navigate = useNavigate();
  const projectService = useProjectService();
  const milestoneService = useProjectMilestoneService();
  const taskService = useTaskPortalTaskService();

  const { data: myProjects = [], isLoading: projectsLoading } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-projects'],
    queryFn: () => projectService.getMyListView(),
  });
  const { data: myTasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-tasks'],
    queryFn: () => taskService.getMyTasks(MY_TASKS_FILTER),
  });
  const { data: milestones = [], isLoading: milestonesLoading } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'milestones'],
    queryFn: () => milestoneService.getAll(),
  });

  const isLoading = projectsLoading || tasksLoading || milestonesLoading;

  const counts = useMemo(() => {
    const active = (myProjects as ProjectModelView[]).filter((p) => !p.isArchived);
    const myProjectIds = new Set(active.map((p) => p.projectID));

    const atRisk = active.filter((p) => {
      const h = computeHealthStatus(p);
      return h === 'At Risk' || h === 'Critical';
    });

    const openTasks = (myTasks as TaskModelView[]).filter((t) =>
      OPEN_TASK_STATUS_IDS.has(t.statusID)
    );

    // Upcoming milestones on my projects, due within the next 30 days.
    const today = dayjs().startOf('day');
    const horizon = today.add(30, 'day');
    const upcomingMilestones = (milestones as ProjectMilestone[]).filter((m) => {
      if (!myProjectIds.has(m.projectID)) return false;
      const due = parseBackendDate(m.dueDate);
      if (!due) return false;
      const d = dayjs(due);
      return (d.isAfter(today) || d.isSame(today, 'day')) && d.isBefore(horizon);
    });

    return {
      activeProjects: active.length,
      openTasks: openTasks.length,
      upcomingMilestones: upcomingMilestones.length,
      atRisk: atRisk.length,
    };
  }, [myProjects, myTasks, milestones]);

  if (isLoading) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={120} radius="md" />
        ))}
      </SimpleGrid>
    );
  }

  const cards = [
    {
      title: 'My Active Projects',
      value: counts.activeProjects,
      icon: <IconFolders size={20} />,
      color: 'indigo',
      to: PROJECTS_ROUTES.MY_PROJECTS,
    },
    {
      title: 'My Open Tasks',
      value: counts.openTasks,
      icon: <IconChecklist size={20} />,
      color: 'blue',
      to: TASKS_ROUTES.MY_TASKS,
    },
    {
      title: 'Upcoming Milestones',
      value: counts.upcomingMilestones,
      icon: <IconFlag size={20} />,
      color: 'teal',
      to: PROJECTS_ROUTES.CALENDAR,
    },
    {
      title: 'At Risk',
      value: counts.atRisk,
      icon: <IconAlertTriangle size={20} />,
      color: 'yellow',
      to: PROJECTS_ROUTES.MY_PROJECTS,
    },
  ];

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
      {cards.map((c, i) => (
        <FadeIn key={c.title} delay={i * 60}>
          <KpiCard
            title={c.title}
            value={c.value}
            icon={c.icon}
            color={c.color as any}
            animate
            onClick={() => navigate(c.to)}
          />
        </FadeIn>
      ))}
    </SimpleGrid>
  );
}
